import { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, ChevronDown, AlertTriangle } from 'lucide-react';
import { useChat } from '../context/ChatContext';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ChatControls } from './ChatControls';
import { WelcomeScreen } from './WelcomeScreen';
import { ErrorDisplay } from './ErrorDisplay';
import { FeedbackModal } from './FeedbackModal';
import { estimateTokens } from '@/lib/tokenEstimator';
import { TOKEN_CONTEXT_LIMIT } from '@/config/appConfig';

interface ChatContentProps {
  onGoBack: () => void;
}

export const ChatContent = ({ onGoBack }: ChatContentProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  // MessageList — and with it the scrollable div — only renders *after* the
  // early returns further down (welcome screen, loader, error states). On the
  // renders where those win, this ref is still null. Effects, though, run on
  // mount regardless: they'd read a null ref, bail out, and — since their deps
  // are stable callbacks — never run again, so the scroll listeners were never
  // attached at all. Mirroring the node into state gives those effects
  // something that actually changes the moment the div appears.
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const attachScrollContainer = useCallback((node: HTMLDivElement | null) => {
    scrollContainerRef.current = node;
    setScrollEl(node);
  }, []);

  // Whether the user is currently at (or near) the bottom of the message list
  const isAtBottomRef = useRef(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const {
    messages,
    input,
    setInput,
    isSending,
    sendError,
    clearSendError,
    sendMessage,
    messagesError,
    clearMessagesError,
    retryMessages,
    activeConversationId,
    isMessagesLoading,
    handleExportTxt,
    handleExportPdf,
    handleCopyToClipboard,
    tokenCount,
    isExhausted,
    startNewConversation,
    streamingText,
    agentStatus,
    queuedMessage,
  } = useChat();

  const isNearLimit = !isExhausted && tokenCount > 0 &&
    (tokenCount + estimateTokens(input)) > TOKEN_CONTEXT_LIMIT * 0.95;

  // ─── Scroll helpers ──────────────────────────────────────────────────────────

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  }, []);

  // Eased, restartable "glide to bottom" — used everywhere we want a smooth
  // (not instant) scroll while a response may still be streaming in. Unlike
  // the native `scrollTo({behavior:'smooth'})`, calling this again while it's
  // already running doesn't cancel/restart the animation: each frame re-reads
  // the *current* scrollHeight, so it keeps gliding smoothly even as new
  // streamed content keeps pushing the target further down. Repeatedly
  // restarting a native smooth-scroll on every chunk is what read as jerky
  // before — this fixes that, and the 0.12 factor below is what to tune for
  // "faster"/"slower".
  const glideRafRef = useRef<number | null>(null);

  const stopGlide = useCallback(() => {
    if (glideRafRef.current !== null) {
      cancelAnimationFrame(glideRafRef.current);
      glideRafRef.current = null;
    }
  }, []);

  const glideToBottom = useCallback(() => {
    if (glideRafRef.current !== null) return; // already gliding
    const step = () => {
      const container = scrollContainerRef.current;
      if (!container || !isAtBottomRef.current) {
        glideRafRef.current = null;
        return;
      }
      const target = container.scrollHeight - container.clientHeight;
      const remaining = target - container.scrollTop;
      if (Math.abs(remaining) < 1) {
        glideRafRef.current = null; // caught up — idle until the next chunk/resume restarts it
        return;
      }
      // Ease by a fixed fraction of the remaining distance per frame instead
      // of jumping straight there — this constant is the "how slow" knob.
      container.scrollTop += remaining * 0.12;
      glideRafRef.current = requestAnimationFrame(step);
    };
    glideRafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => stopGlide, [stopGlide]); // cancel any in-flight glide on unmount

  // Visibility of the "jump to bottom" arrow. This NEVER re-arms auto-follow.
  //
  // It used to: "if the user scrolls back to the bottom on their own, resume
  // following". That quietly broke the whole pause, because auto-follow leaves
  // the user exactly at the bottom — so the first ~80px of their own escape
  // gesture landed inside the "you came back" zone and re-armed following
  // while their finger was still down. The next chunk then yanked them back.
  // Scroll position is now purely cosmetic; only the arrow re-arms (and
  // sending a new message, which is just as explicit).
  const refreshScrollButton = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    // While following, we're already heading to the bottom — no arrow, and no
    // flicker when a big block lands and the glide has ground to cover.
    if (isAtBottomRef.current) {
      setShowScrollButton(false);
      return;
    }
    // 80px of slack absorbs rounding differences.
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollButton(distanceFromBottom >= 80);
  }, []);

  useEffect(() => {
    if (!scrollEl) return;
    scrollEl.addEventListener('scroll', refreshScrollButton, { passive: true });
    return () => scrollEl.removeEventListener('scroll', refreshScrollButton);
  }, [scrollEl, refreshScrollButton]);

  // Intent-based stop: the moment the user reaches for the scrollable area
  // (finger down, mouse wheel, scrollbar drag) auto-follow is switched OFF and
  // stays off. Not "paused while they hold" — off. The view will not move on
  // its own again: not when they let go, not on the next chunk, not when the
  // response ends. From here the only things that scroll are the user's own
  // gesture and the arrow below. Deliberately not position-based: the user
  // starts their gesture from the bottom, so any "are we near the bottom?"
  // test would fire mid-gesture and undo the stop.
  useEffect(() => {
    if (!scrollEl) return;

    const pause = () => {
      isAtBottomRef.current = false;
      stopGlide();
    };

    scrollEl.addEventListener('touchstart', pause, { passive: true });
    scrollEl.addEventListener('mousedown', pause, { passive: true });
    scrollEl.addEventListener('wheel', pause, { passive: true });

    return () => {
      scrollEl.removeEventListener('touchstart', pause);
      scrollEl.removeEventListener('mousedown', pause);
      scrollEl.removeEventListener('wheel', pause);
    };
  }, [scrollEl, stopGlide]);

  // When a new conversation is loaded from history: jump instantly to the bottom
  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom('instant');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId]);

  // Track isSending transitions to scroll at the right moment
  const prevIsSendingRef = useRef(false);
  useEffect(() => {
    if (!prevIsSendingRef.current && isSending) {
      // Just started sending: DOM has the new user message + mascot — scroll to reveal them
      scrollToBottom('instant');
      isAtBottomRef.current = true;
      setShowScrollButton(false);
    }
    if (prevIsSendingRef.current && !isSending) {
      // Response arrived: scroll to bottom if user was already there
      if (isAtBottomRef.current) {
        glideToBottom();
      }
    }
    prevIsSendingRef.current = isSending;
  }, [isSending, scrollToBottom, glideToBottom]);

  // While streaming text grows, keep gliding toward the bottom — but only if
  // the user hasn't taken over (see the intent-based effect above).
  useEffect(() => {
    if (!streamingText) return;
    if (isAtBottomRef.current) {
      glideToBottom();
    } else {
      // Stopped: the response keeps growing below the fold. Content growing
      // does NOT fire a scroll event, so without this the arrow would never
      // appear for someone who tapped to stop while already at the bottom.
      refreshScrollButton();
    }
  }, [streamingText, glideToBottom, refreshScrollButton]);

  // ─── Send handler ─────────────────────────────────────────────────────────────

  const handleSend = (value: string) => {
    // Mark as "at bottom" so the isSending effect will scroll after render
    isAtBottomRef.current = true;
    setShowScrollButton(false);
    void sendMessage(value);
  };

  // ─── Early returns ────────────────────────────────────────────────────────────

  if (sendError) {
    return <ErrorDisplay message={sendError} onRetry={clearSendError} onGoBack={onGoBack} />;
  }

  if (messagesError) {
    return (
      <ErrorDisplay
        message={messagesError}
        onRetry={() => {
          clearMessagesError();
          void retryMessages();
        }}
        onGoBack={onGoBack}
      />
    );
  }

  if (isMessagesLoading && activeConversationId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 text-slate-500">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm">Loading messages...</p>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <WelcomeScreen
        input={input}
        onInputChange={setInput}
        onSendMessage={handleSend}
        isLoading={isSending}
        onExportTxt={handleExportTxt}
        onExportPdf={handleExportPdf}
        onCopyToClipboard={handleCopyToClipboard}
      />
    );
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden min-h-0">
      <MessageList
        messages={messages}
        isLoading={isSending}
        streamingText={streamingText}
        agentStatus={agentStatus}
        queuedMessage={queuedMessage}
        scrollContainerRef={attachScrollContainer}
        bottomRef={bottomRef}
      />

      {/* Jump-to-bottom button — appears when user has scrolled up */}
      {showScrollButton && (
        <button
          onClick={() => {
            isAtBottomRef.current = true;
            setShowScrollButton(false);
            glideToBottom();
          }}
          className="absolute bottom-20 left-1/2 -translate-x-1/2 md:bottom-24 z-10 flex items-center justify-center w-8 h-8 rounded-full bg-white border border-slate-200 shadow-md hover:bg-slate-50 transition-all"
          aria-label="Scroll to latest message"
        >
          <ChevronDown className="h-4 w-4 text-slate-600" />
        </button>
      )}

      <div className="w-full px-0 md:px-6 pt-3 pb-0 md:pb-2 bg-white flex-shrink-0">
        {isExhausted && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <span className="font-semibold">Context limit reached.</span> This conversation cannot continue.{' '}
              <button
                onClick={startNewConversation}
                className="underline font-medium hover:text-red-900 transition-colors"
              >
                Start a new conversation
              </button>
            </div>
          </div>
        )}
        {isNearLimit && (
          <div className="mb-2 flex items-center gap-1.5 text-xs text-orange-600">
            <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
            <span>Warning: this message may exceed the context limit.</span>
          </div>
        )}
        <MessageInput
          value={input}
          onChange={setInput}
          onSend={handleSend}
          isLoading={isSending}
          disabled={isExhausted}
        />
        <ChatControls
          onExportTxt={handleExportTxt}
          onExportPdf={handleExportPdf}
          onCopyToClipboard={handleCopyToClipboard}
          onSendFeedback={() => setIsFeedbackOpen(true)}
          hasMessages={messages.length > 0}
          hasConversation={activeConversationId !== null}
          tokenCount={tokenCount}
          tokenLimit={TOKEN_CONTEXT_LIMIT}
        />
      </div>
      <FeedbackModal
        isOpen={isFeedbackOpen}
        conversationId={activeConversationId}
        onClose={() => setIsFeedbackOpen(false)}
      />
    </div>
  );
};

